import React from 'react';

// Z 平面迷你图（距离保护）：渲染各段阻抗扇形
export const ZPlaneMini = ({ zones = [], lineAngleDeg = 75 }) => {
  const width = 720;
  const height = 360;
  const padding = { top: 10, right: 20, bottom: 28, left: 28 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const cx = padding.left + innerW / 2;
  const cy = padding.top + innerH / 2;
  const maxZ = Math.max(1, ...zones.map(z => Number(z.Z) || 0));
  const Rmax = Math.min(innerW, innerH) / 2 - 6;
  const rScale = (Z) => (Number(Z) || 0) / maxZ * Rmax;
  const deg2rad = (d) => d * Math.PI / 180;
  const polarPoint = (r, deg) => {
    const a = deg2rad(deg);
    return { x: cx + r * Math.cos(a), y: cy - r * Math.sin(a) };
  };
  const arcPath = (r, startDeg, endDeg) => {
    const p0 = polarPoint(r, startDeg);
    const p1 = polarPoint(r, endDeg);
    const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    const sweep = endDeg > startDeg ? 1 : 0;
    const path = `M ${cx} ${cy} L ${p0.x} ${p0.y} A ${r} ${r} 0 ${largeArc} ${sweep} ${p1.x} ${p1.y} Z`;
    return path;
  };

  const axesLen = Rmax + 12;

  return (
    <svg className="h-plane-svg" width={width} height={height}>
      <rect x={0} y={0} width={width} height={height} fill="#fff" />
      {/* 轴线：R 向右、X 向上 */}
      <line x1={cx - axesLen} y1={cy} x2={cx + axesLen} y2={cy} stroke="#999" />
      <line x1={cx} y1={cy + axesLen} x2={cx} y2={cy - axesLen} stroke="#999" />
      <text x={cx + axesLen - 18} y={cy - 6} fontSize="12" fill="#666">R</text>
      <text x={cx + 4} y={cy - axesLen + 16} fontSize="12" fill="#666">X</text>

      {/* 中心阻抗角参考线 */}
      {typeof lineAngleDeg === 'number' && isFinite(lineAngleDeg) && (
        (() => {
          const pDir = polarPoint(Rmax + 8, lineAngleDeg);
          return (
            <g>
              <line x1={cx} y1={cy} x2={pDir.x} y2={pDir.y} stroke="#c5c5c5" strokeDasharray="4,3" />
              <text x={pDir.x + 4} y={pDir.y} fontSize="12" fill="#666">θ≈{Number(lineAngleDeg).toFixed(1)}°</text>
            </g>
          );
        })()
      )}

      {/* 分段扇形 */}
      {zones.map((z, i) => {
        const r = rScale(z.Z);
        const startDeg = Number(z.thetaStartDeg);
        const endDeg = Number(z.thetaEndDeg);
        const color = ['#1677ff','#52c41a','#faad14','#eb2f96'][i % 4];
        return (
          <g key={i}>
            <path d={arcPath(r, startDeg, endDeg)} fill={color} fillOpacity={0.12} stroke={color} strokeOpacity={0.8} />
            <text x={cx + 8} y={cy + 18 + i * 16} fontSize="12" fill={color}>
              {z.label || `Zone ${i+1}`} · Z={Number(z.Z).toFixed(3)}Ω · θ={Number(startDeg).toFixed(0)}~{Number(endDeg).toFixed(0)}°{z.marginPct != null ? ` · 裕度${z.marginPct}%` : ''}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// 差动 Id–Ir 迷你图：按 segments 分段绘制比率制动线，并标注阈值与速断；显示二次谐波闭锁系数 Kb
export const IdIrMini = ({ characteristic = {}, kb = null }) => {
  const width = 720;
  const height = 360;
  const padding = { top: 10, right: 20, bottom: 28, left: 36 };
  const slope = Number(characteristic.slope);
  const diffPickup = Number(characteristic.diffPickup) || 0.4;
  const inst = Number(characteristic.instantaneousSecondary) || 2.4;
  const rawSegs = Array.isArray(characteristic.segments)
    ? characteristic.segments.map(seg => ({ slope: Number(seg.slope), threshold: Number(seg.threshold), label: seg.label }))
    : [];
  const segsSorted = rawSegs.filter(sg => (isFinite(sg.threshold) || sg.threshold === Infinity) && !Number.isNaN(sg.slope)).sort((a, b) => (a.threshold === b.threshold ? 0 : ((a.threshold || 0) - (b.threshold || 0))));
  const maxThreshold = Math.max(0, ...segsSorted.map(sg => (isFinite(sg.threshold) ? sg.threshold : 0)));
  const IrMax = Math.max(5, inst * 2, diffPickup * 10, maxThreshold * 1.1);
  const kLast = isFinite(slope) ? slope : (segsSorted.length > 0 && isFinite(segsSorted[segsSorted.length - 1].slope) ? segsSorted[segsSorted.length - 1].slope : 0.5);
  const IdMax = Math.max(inst * 1.2, diffPickup * 4, kLast * IrMax + diffPickup);
  const xScale = (Ir) => padding.left + (Ir / IrMax) * (width - padding.left - padding.right);
  const yScale = (Id) => (height - padding.bottom) - (Id / IdMax) * (height - padding.top - padding.bottom);

  // 组装分段（与叠加层逻辑一致）：如未提供 segments 或首段阈值大于 diffPickup，则补 K=0 段至 diffPickup；末段延伸到 Infinity
  const segs = (() => {
    const arr = [];
    if (diffPickup > 0 && (segsSorted.length === 0 || (isFinite(segsSorted[0].threshold) ? (segsSorted[0].threshold > diffPickup) : true))) {
      arr.push({ slope: 0, threshold: diffPickup, label: 'K=0' });
    }
    arr.push(...segsSorted);
    arr.push({ slope: kLast, threshold: Infinity, label: `K=${isFinite(kLast) ? kLast : '?'}` });
    return arr;
  })();

  const xTicks = Array.from({ length: 6 }).map((_, i) => (i * IrMax / 5));
  const yTicks = Array.from({ length: 6 }).map((_, i) => (i * IdMax / 5));

  const segElems = [];
  let Ir_s = 0;
  let Id_s = diffPickup;
  for (let si = 0; si < segs.length; si++) {
    const k = Number(segs[si].slope) || 0;
    const Ir_e_raw = segs[si].threshold;
    const Ir_e = Math.min(isFinite(Ir_e_raw) ? Ir_e_raw : IrMax, IrMax);
    if (Ir_e <= Ir_s) continue;
    const Id_e = Id_s + k * (Ir_e - Ir_s);
    segElems.push(
      <path key={`seg-${si}`} d={`M ${xScale(Ir_s)},${yScale(Id_s)} L ${xScale(Ir_e)},${yScale(Id_e)}`} stroke="#1677ff" strokeWidth={1.5} strokeDasharray={k === 0 ? '4,3' : '3,2'} fill="none" opacity={0.9} />
    );
    if (isFinite(Ir_e_raw) && Ir_e < IrMax - 1e-6) {
      segElems.push(<line key={`th-${si}`} x1={xScale(Ir_e)} y1={padding.top} x2={xScale(Ir_e)} y2={height - padding.bottom} stroke="#e9e9e9" strokeDasharray="4,3" />);
      segElems.push(<text key={`thlab-${si}`} x={xScale(Ir_e) - 18} y={height - padding.bottom + 16} fontSize="12" fill="#888">Ir≈{Ir_e.toFixed(2)}A</text>);
    }
    const Ir_mid = Ir_s + (Ir_e - Ir_s) / 2;
    const Id_mid = Id_s + k * (Ir_mid - Ir_s);
    segElems.push(<text key={`klab-${si}`} x={xScale(Ir_mid) + 6} y={yScale(Id_mid) - 6} fontSize="12" fill="#1677ff">{segs[si].label || `K=${isFinite(k) ? k.toFixed(2) : '?'}`}</text>);
    Ir_s = Ir_e; Id_s = Id_e;
    if (Ir_s >= IrMax - 1e-6) break;
  }

  const instY = yScale(inst);

  return (
    <svg className="h-plane-svg" width={width} height={height}>
      <rect x={0} y={0} width={width} height={height} fill="#fff" />
      <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#999" />
      <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#999" />
      {/* 坐标系与 Kb 提示 */}
      <g>
        <rect x={padding.left + 6} y={padding.top + 4} width={kb != null ? 220 : 140} height={kb != null ? 40 : 24} rx={6} ry={6} fill="#fafafa" stroke="#e6e6e6" />
        <text x={padding.left + 14} y={padding.top + 20} fontSize="12" fill="#444">坐标系: Id–Ir</text>
        {kb != null && (
          <text x={padding.left + 14} y={padding.top + 34} fontSize="12" fill="#666">Kb≈{Number(kb).toFixed(2)}（二次谐波闭锁）</text>
        )}
      </g>
      {/* 分段比率制动线 */}
      <g>{segElems}</g>
      {/* 速断水平线 */}
      <line x1={padding.left} y1={instY} x2={width - padding.right} y2={instY} stroke="#fa541c" strokeDasharray="6,4" />
      <text x={padding.left + 6} y={instY - 6} fontSize="12" fill="#fa541c">Id速断≈{inst.toFixed(2)}A</text>
      {/* 网格刻度 */}
      {xTicks.map((Ir, i) => (
        <g key={`x${i}`}>
          <line x1={xScale(Ir)} y1={height - padding.bottom} x2={xScale(Ir)} y2={height - padding.bottom - 5} stroke="#aaa" />
          <text x={xScale(Ir) - 10} y={height - padding.bottom + 16} fontSize="12" fill="#666">{Ir.toFixed(1)}</text>
        </g>
      ))}
      {yTicks.map((Id, i) => (
        <g key={`y${i}`}>
          <line x1={padding.left} y1={yScale(Id)} x2={padding.left + 5} y2={yScale(Id)} stroke="#aaa" />
          <text x={8} y={yScale(Id) + 4} fontSize="12" fill="#666">{Id.toFixed(1)}</text>
        </g>
      ))}
    </svg>
  );
};

// 条形图迷你组件：展示数值型结果 Top5
export const BarChartMini = ({
  data = [],
  width = 720,
  height = 300,
  labelMode = 'wrap', // 'wrap' | 'rotate'
  rotateAngle = -30,
  wrapMaxChars = 12
}) => {
  // data: [{ label, value }]
  // 为标签预留更大底部边距，防止遮挡（两行/旋转均适用）
  const padding = { top: 10, right: 20, bottom: 64, left: 40 };
  const palette = ['#1677ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1', '#13c2c2', '#2f54eb'];
  const maxVal = Math.max(1, ...data.map(d => Number(d.value) || 0));
  const barWidth = (width - padding.left - padding.right) / Math.max(1, data.length);
  const scaleY = (v) => (height - padding.bottom) - (v / maxVal) * (height - padding.top - padding.bottom);

  // 轴与网格刻度：根据数据推断单位类型
  const unitType = (() => {
    const labels = data.map(d => String(d.label || ''));
    if (labels.some(s => /(时间|时限|time|tms)/i.test(s))) return 'time';
    if (labels.some(s => /(电流|current|ir|id)/i.test(s))) return 'current';
    if (labels.some(s => /(阻抗|impedance|ohm|Ω)/i.test(s))) return 'impedance';
    return 'generic';
  })();
  const proxyLabel = unitType === 'time' ? 'time' : unitType === 'current' ? 'current' : unitType === 'impedance' ? 'Ω' : '';
  const yTicks = Array.from({ length: 6 }).map((_, i) => (i * maxVal / 5));

  const pickColor = (label, i) => {
    const s = String(label || '').toLowerCase();
    if (/(电流|current|ir|id)/i.test(s)) return '#1677ff';
    if (/(时间|时限|time|tms)/i.test(s)) return '#fa541c';
    if (/(阻抗|impedance|ohm|Ω)/i.test(s)) return '#722ed1';
    if (/(裕度|margin)/i.test(s)) return '#52c41a';
    return palette[i % palette.length];
  };

  const formatValue = (value, label) => {
    const vRaw = Number(value) || 0;
    const s = String(label || '').toLowerCase();
    // 规则：时间->s/ms；电流->A/kA；阻抗->Ω/kΩ；默认千分位缩放
    if (/(时间|时限|time)/i.test(s)) {
      if (vRaw < 1) return `${(vRaw * 1000).toFixed(0)}ms`;
      return `${vRaw.toFixed(2)}s`;
    }
    if (/(电流|current|ir|id)/i.test(s)) {
      if (Math.abs(vRaw) >= 1000) return `${(vRaw / 1000).toFixed(2)}kA`;
      return `${vRaw.toFixed(2)}A`;
    }
    if (/(阻抗|impedance|ohm|Ω)/i.test(s)) {
      if (Math.abs(vRaw) >= 1000) return `${(vRaw / 1000).toFixed(2)}kΩ`;
      return `${vRaw.toFixed(2)}Ω`;
    }
    // fallback：普通数值，>=1000 用 k/M 缩放
    if (Math.abs(vRaw) >= 1000) {
      const units = ['', 'k', 'M', 'G'];
      let val = vRaw;
      let idx = 0;
      while (Math.abs(val) >= 1000 && idx < units.length - 1) { val /= 1000; idx++; }
      return `${val.toFixed(2)}${units[idx]}`;
    }
    return vRaw.toFixed(2);
  };

  const fullLabel = (label) => String(label || '');
  const splitLabel = (label) => {
    const s = fullLabel(label).trim();
    if (labelMode !== 'wrap') return [s];
    if (s.length <= wrapMaxChars) return [s];
    // 优先在空格处分割，否则按长度硬拆分为两行
    const idx = (() => {
      const spaceIdx = s.lastIndexOf(' ', wrapMaxChars);
      if (spaceIdx > 0) return spaceIdx;
      return wrapMaxChars;
    })();
    const first = s.slice(0, idx).trim();
    const second = s.slice(idx).trim();
    return [first, second];
  };

  return (
    <svg
      className="h-bar-svg"
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMinYMin meet"
    >
      <rect x={0} y={0} width={width} height={height} fill="#fff" />
      {/* 轴线：X 轴与 Y 轴 */}
      <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#999" />
      <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#999" />
      {/* Y 轴网格与刻度 */}
      {yTicks.map((v, i) => (
        <g key={`yt${i}`}>
          <line x1={padding.left} y1={scaleY(v)} x2={width - padding.right} y2={scaleY(v)} stroke="#eee" />
          <line x1={padding.left} y1={scaleY(v)} x2={padding.left + 5} y2={scaleY(v)} stroke="#bbb" />
          <text x={8} y={scaleY(v) + 4} fontSize="12" fill="#666">{formatValue(v, proxyLabel)}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const x = padding.left + i * barWidth + barWidth * 0.1;
        const y = scaleY(Number(d.value) || 0);
        const h = (height - padding.bottom) - y;
        const color = pickColor(d.label, i);
        const innerW = barWidth * 0.8;
        const xLab = x + innerW; // 旋转模式靠右对齐
        const yLab = height - padding.bottom + 16;
        const xLabCenter = x + innerW / 2; // 换行模式居中对齐
        const lines = splitLabel(d.label);
        return (
          <g key={i}>
            <rect x={x} y={y} width={barWidth * 0.8} height={h} fill={color} opacity={0.85} />
            {/* 原生悬浮提示：完整标签与格式化值 */}
            <title>{`${fullLabel(d.label)}: ${formatValue(d.value, d.label)}`}</title>
            <text x={x} y={y - 4} fontSize="12" fill="#444">{formatValue(d.value, d.label)}</text>
            {/* 标签渲染模式：wrap（默认）或 rotate */}
            {labelMode === 'rotate' ? (
              <text
                transform={`translate(${xLab}, ${yLab}) rotate(${rotateAngle})`}
                fontSize="12"
                fill="#666"
                textAnchor="end"
              >
                {fullLabel(d.label)}
              </text>
            ) : (
              <g>
                <text x={xLabCenter} y={yLab} fontSize="12" fill="#666" textAnchor="middle">{lines[0]}</text>
                {lines[1] && (
                  <text x={xLabCenter} y={yLab + 13} fontSize="12" fill="#666" textAnchor="middle">{lines[1]}</text>
                )}
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export default { ZPlaneMini, IdIrMini, BarChartMini };